import { Box, TextField } from "@mui/material"
import FormControl from "@mui/material/FormControl"
import FormLabel from "@mui/material/FormLabel"
import { useEffect, useState, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { UText } from "@/components/index"
import { useAppDispatch } from "@/core/app/hooks"
import { debounce } from "lodash"

interface DPAModelPrintProps {
  currPrint: any
  updateModel: any
}

export const DPAModelPrint = ({
  currPrint,
  updateModel,
}: DPAModelPrintProps) => {
  const { t } = useTranslation("common")
  const [offesetDPA, setOffesetDPA] = useState<number | string>(0)
  const [thicknessDPA, setThicknessDPA] = useState<number | string>(0.5)
  const dispatch = useAppDispatch()

  // Create update functions that immediately update the store
  const updateOffset = useCallback((val) => {
    const numVal = parseFloat(val)
    if (isNaN(numVal)) return
    const clamped = Math.min(Math.max(numVal, -0.4), 0.4)
    const finalValue = parseFloat(clamped.toFixed(2))
    dispatch(updateModel({ DPAOffset: finalValue }))
  }, [dispatch, updateModel])

  const updateThickness = useCallback((val) => {
    const numVal = parseFloat(val)
    if (isNaN(numVal)) return
    const clamped = Math.min(Math.max(numVal, 0.31), 1.5)
    const finalValue = parseFloat(clamped.toFixed(2))
    dispatch(updateModel({ DPAthickness: finalValue }))
  }, [dispatch, updateModel])

  // Create debounced versions for the input handlers
  const debouncedUpdateOffset = useCallback(debounce(updateOffset, 500), [updateOffset])
  const debouncedUpdateThickness = useCallback(debounce(updateThickness, 500), [updateThickness])

  const handleInput = (e, min, max, setVal, callback, allowNegative = false) => {
    let value = e.target.value

    // Sanitize input ---
    if (allowNegative) {
      value = value
        .replace(/[^0-9.-]/g, "") // allow numbers, dots, and minus signs
        .replace(/(?!^)-/g, "") // only allow minus at the beginning
        .replace(/(\..*)\./g, "$1") // allow only one dot
    } else {
      value = value
        .replace(/[^0-9.]/g, "") // remove all non-numeric/dot characters
        .replace(/(\..*)\./g, "$1") // allow only one dot
        .replace(/^\./, `${min}.`) // if input starts with ".", prepend min
    }

    // Restrict format (only 2 decimal places) ---
    value = value.replace(/(\.\d{2})\d+$/, "$1")

    // Allow empty value during typing
    if (value === "") {
      setVal("")
      return
    }

    // Update state and call debounced callback
    const numValue = parseFloat(value)
    setVal(value)
    if (!isNaN(numValue)) {
      callback?.(value)
    }
  }

  useEffect(() => {
    if (currPrint?.DPAOffset !== undefined) {
      setOffesetDPA(currPrint?.DPAOffset)
    }
    if (currPrint?.DPAthickness !== undefined) {
      setThicknessDPA(currPrint?.DPAthickness)
    }
  }, [currPrint?.DPAOffset, currPrint?.DPAthickness])

  const styleObj = {
    height: "80px",
    borderRadius: "6px",
    flexDirection: "row",
  }

  return (
    <Box
      id="other4"
      sx={{
        display: "flex",
        ...styleObj,
        height: "auto",
      }}
    >
      <Box sx={{ width: "100%" }}>
        <UText
          variant="subtitle1"
          sxProp={{
            color: "rgba(0,0,0,0.7)",
            fontSiz: "16px",
          }}
        >
          {t(`stlPrinting.DPA`)}
        </UText>
        <Box
          sx={{
            display: "flex",
            gap: "24px",
            width: "100%",
            marginTop: "8px",
          }}
        >
          <FormControl
            sx={{
              height: "80px",
              display: "flex",
              flex: 1,
            }}
          >
            <FormLabel>{t(`stlPrinting.Offset`)}</FormLabel>
            <TextField
              sx={{
                width: "100px",
                "& .MuiInputBase-input": { padding: "16px 12px" },
              }}
              value={offesetDPA}
              onChange={(e) => {
                handleInput(e, -0.4, 0.4, setOffesetDPA, debouncedUpdateOffset, true)
              }}
            />
          </FormControl>
          <FormControl sx={{ display: "flex", height: "80px", flex: 1 }}>
            <FormLabel>{t(`stlPrinting.thickness`)}</FormLabel>
            <TextField
              sx={{
                width: "100px",
                "& .MuiInputBase-input": { padding: "16px 12px" },
              }}
              value={thicknessDPA}
              onChange={(e) => {
                handleInput(e, 0.31, 1.5, setThicknessDPA, debouncedUpdateThickness)
              }}
            />
          </FormControl> 
        </Box>
      </Box>
    </Box>
  )
}
